/**
 * Calendar Service - Fetches historical events, birthdays, and holidays
 * Uses Wikimedia "On This Day" API - completely free, no API key required
 */

const WIKIMEDIA_API_BASE = 'https://api.wikimedia.org/feed/v1/wikipedia/en/onthisday'

export class CalendarService {
  constructor() {
    this.cache = new Map()
    this.cacheExpiry = 24 * 60 * 60 * 1000 // 24 hours in milliseconds
  }

  /**
   * Get cache key for a specific date
   */
  getCacheKey(month, day, type = 'all') {
    return `${type}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`
  }

  /**
   * Check if cached data is still valid
   */
  isCacheValid(cacheEntry) {
    return cacheEntry && (Date.now() - cacheEntry.timestamp) < this.cacheExpiry
  }

  /**
   * Fetch events from Wikimedia API
   */
  async fetchFromAPI(month, day, type = 'all') {
    const paddedMonth = month.toString().padStart(2, '0')
    const paddedDay = day.toString().padStart(2, '0')
    const url = `${WIKIMEDIA_API_BASE}/${type}/${paddedMonth}/${paddedDay}`

    try {
      console.log(`📅 Fetching calendar data: ${url}`)
      const response = await fetch(url, {
        headers: {
          'User-Agent': 'PinkPixelOS/2.1.0 (https://pinkpixel.dev)',
          'Accept': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error(`Calendar API responded with ${response.status}: ${response.statusText}`)
      }

      const data = await response.json()
      console.log(`📅 Calendar API response:`, data)
      return data
    } catch (error) {
      console.error('Calendar API error:', error)
      throw error
    }
  }

  /**
   * Get events for a specific date with caching
   */
  async getEventsForDate(month, day, type = 'all') {
    const cacheKey = this.getCacheKey(month, day, type)
    const cached = this.cache.get(cacheKey)

    // Return cached data if valid
    if (this.isCacheValid(cached)) {
      console.log(`📅 Using cached calendar data for ${month}/${day}`)
      return cached.data
    }

    try {
      // Fetch fresh data
      const data = await this.fetchFromAPI(month, day, type)
      
      // Cache the result
      this.cache.set(cacheKey, {
        data,
        timestamp: Date.now()
      })

      return data
    } catch (error) {
      // If API fails and we have cached data, use it even if expired
      if (cached) {
        console.log(`📅 Using expired cache due to API error for ${month}/${day}`)
        return cached.data
      }
      
      // Return demo data as fallback
      return this.getDemoData(month, day)
    }
  }

  /**
   * Get today's events
   */
  async getTodaysEvents() {
    const today = new Date()
    return this.getEventsForDate(today.getMonth() + 1, today.getDate())
  }

  /**
   * Get events for the calendar widget (simplified format)
   */
  async getWidgetEvents() {
    try {
      const events = await this.getTodaysEvents()
      const widget = []

      // Add selected historical events (limit to 2 for widget)
      if (events.selected && events.selected.length > 0) {
        const selectedEvent = events.selected[0]
        widget.push({
          type: 'event',
          title: 'Historical Event',
          description: selectedEvent.text, // Show full text instead of truncating
          time: 'Today in History',
          icon: '📜',
          color: 'blue'
        })
      }

      // Add notable births (limit to 1 for widget)
      if (events.births && events.births.length > 0) {
        const birth = events.births[0]
        const person = birth.pages[0]
        widget.push({
          type: 'birthday',
          title: 'Famous Birthday',
          description: person.titles.normalized,
          time: `Born ${birth.year || 'unknown'}`,
          icon: '🎂',
          color: 'pink'
        })
      }

      // If no events, show encouraging message
      if (widget.length === 0) {
        widget.push({
          type: 'info',
          title: 'On This Day',
          description: 'Discover what happened today in history!',
          time: 'Click to explore',
          icon: '📅',
          color: 'purple'
        })
      }

      return widget
    } catch (error) {
      console.error('Error fetching widget events:', error)
      return this.getDemoWidgetData()
    }
  }

  /**
   * Process events for full calendar app
   */
  processEventsForApp(data) {
    const processed = {
      selected: [],
      events: [],
      births: [],
      deaths: [],
      holidays: []
    }

    // Process different types of events
    if (data.selected) {
      processed.selected = data.selected.map(event => ({
        text: event.text,
        pages: event.pages || [],
        year: event.year,
        description: event.pages?.[0]?.extract || event.text
      }))
    }

    if (data.events) {
      processed.events = data.events.map(event => ({
        text: event.text,
        year: event.year,
        pages: event.pages || []
      }))
    }

    if (data.births) {
      processed.births = data.births.map(birth => ({
        text: birth.text,
        year: birth.year,
        pages: birth.pages || [],
        person: birth.pages?.[0]?.titles?.normalized || 'Unknown'
      }))
    }

    if (data.deaths) {
      processed.deaths = data.deaths.map(death => ({
        text: death.text,
        year: death.year,
        pages: death.pages || [],
        person: death.pages?.[0]?.titles?.normalized || 'Unknown'
      }))
    }

    if (data.holidays) {
      processed.holidays = data.holidays.map(holiday => ({
        text: holiday.text,
        pages: holiday.pages || []
      }))
    }

    return processed
  }

  /**
   * Truncate text to specified length
   */
  truncateText(text, maxLength) {
    if (text.length <= maxLength) return text
    return text.substring(0, maxLength).trim() + '...'
  }

  /**
   * Demo data for fallback
   */
  getDemoData(month, day) {
    return {
      selected: [{
        text: `Discover what happened on ${month}/${day} throughout history!`,
        pages: []
      }],
      births: [{
        text: "Notable people were born on this day in history.",
        pages: [{ titles: { normalized: "Historical Figure" } }]
      }],
      events: [{
        text: "Significant events occurred on this day.",
        pages: []
      }]
    }
  }

  /**
   * Demo widget data for fallback
   */
  getDemoWidgetData() {
    return [
      {
        type: 'event',
        title: 'Historical Event',
        description: 'Discover what happened today!',
        time: 'Today in History',
        icon: '📜',
        color: 'blue'
      },
      {
        type: 'birthday',
        title: 'Famous Birthday',
        description: 'Notable people born today',
        time: 'Throughout history',
        icon: '🎂',
        color: 'pink'
      }
    ]
  }

  /**
   * Clear cache (useful for development/debugging)
   */
  clearCache() {
    this.cache.clear()
    console.log('📅 Calendar cache cleared')
  }
}

// Export singleton instance
export const calendarService = new CalendarService()
export default calendarService
